import {
  EnvironmentProviders,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { Store } from '@ngrx/store';
import * as Sentry from '@sentry/angular';
import * as LaunchDarkly from 'launchdarkly-js-client-sdk';
import { environment } from '../../environments/environment';
import { FeatureFlagsActions } from './store/feature-flags.actions';
import { FeatureFlagsState } from './store/feature-flags.reducer';

export type LaunchDarklyClient = LaunchDarkly.LDClient;
export type LaunchDarklyContext = LaunchDarkly.LDContext;
export type LaunchDarklyFlagValue = LaunchDarkly.LDFlagValue;
export type LaunchDarklyFlagSet = LaunchDarkly.LDFlagSet;

/**
 * How to configure LaunchDarkly SDKs to send application metadata to LaunchDarkly:
 * https://docs.launchdarkly.com/sdk/features/app-config
 */
export let ldClient: LaunchDarklyClient | undefined;

export const provideLaunchDarklyInitializer = (): EnvironmentProviders =>
  provideAppInitializer(() =>
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    initializeLaunchDarkly(inject(Store)),
  );

const initializeLaunchDarkly = async (
  featureFlagStore: Store<FeatureFlagsState>,
): Promise<void> => {
  // eslint-disable-next-line no-console
  console.log('[LaunchDarkly] LaunchDarkly client initialization');
  try {
    ldClient = LaunchDarkly.initialize(
      environment.launchDarkly.envKey,
      environment.launchDarkly.context,
      {
        ...environment.launchDarkly.options,
        inspectors: [Sentry.buildLaunchDarklyFlagUsedHandler()],
      },
    );
    await ldClient.waitForInitialization();

    featureFlagStore.dispatch(
      FeatureFlagsActions.loadFeatureFlags({
        featureFlags: Object.entries(ldClient.allFlags()).map(
          ([key, value]: [string, LaunchDarklyFlagValue]) => {
            return {
              key,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              value,
            };
          },
        ),
      }),
    );
    /**
     * How the change event works in the LaunchDarkly SDKs:
     * https://docs.launchdarkly.com/sdk/features/flag-changes#javascript
     */
    ldClient.on('change', (flags: LaunchDarklyFlagSet) => {
      featureFlagStore.dispatch(
        FeatureFlagsActions.onChangeSyncSuccess({
          featureFlags: Object.entries(flags).map(([key, value]) => ({
            key,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            value: (value?.current as boolean) || false,
          })),
        }),
      );
    });

    ldClient.on('error', (error: Error) => {
      featureFlagStore.dispatch(FeatureFlagsActions.onError({ error }));
    });
  } catch (error: unknown) {
    Sentry.captureException(error);
  }
};
